// Models
import { IExerciseData } from 'storage/exercise/models'
import {
  IFormInputs,
  IModelInput,
  IWorkoutSetInput,
  TFormValues,
  TMuscleGroupFilter,
  TSetSpecialSet,
  TSpecialSetInput,
} from 'models'

// React
import { FC } from 'react'

// Libraries
import { UseFormReturn, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

// Misc
import { workoutSpecialSetSchema } from 'schemas'

// Components
import {
  WorkoutModelsSets,
  WorkoutModelsRoutines,
  WorkoutModelsSpecialSet,
} from 'views/dashboard/workout'

type Props = {
  addExerciseToWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    exercise: IExerciseData,
  ) => void
  addWorkoutModel: (model: IModelInput) => void
  addWorkoutSet: (modelId: string, set: IWorkoutSetInput) => void
  changePage: (page: string) => void
  filters: TMuscleGroupFilter[]
  inputValue: string
  loadMoreExercises: () => void
  methods: UseFormReturn<IFormInputs, TFormValues>
  mobilePage?: string | null
  onChangeFilters: (filters: TMuscleGroupFilter[]) => void
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  removeExerciseFromWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    exerciseIndex: number,
  ) => void
  removeFilter: (label: string) => void
  removeWorkoutModel: (id: string) => void
  removeWorkoutSet: (modelId: string, workoutSetId: string) => void
  replicateWorkoutSetFields: (modelId: string, set: IWorkoutSetInput) => void
  selectWorkoutModel: (index: number) => void
  selectedModelIndex: number
  setSpecialSet: TSetSpecialSet
  specialSet?: TSpecialSetInput
  stateModels: IModelInput[]
  updateWorkoutModel: (id: string, updatedModel: IModelInput) => void
  updateWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    updatedWorkoutSet: IWorkoutSetInput,
  ) => void
}

const Mobile: FC<Props> = ({
  addExerciseToWorkoutSet,
  addWorkoutModel,
  addWorkoutSet,
  changePage,
  filters,
  inputValue,
  loadMoreExercises,
  methods,
  mobilePage,
  onChangeFilters,
  onSearchChange,
  removeExerciseFromWorkoutSet,
  removeFilter,
  removeWorkoutModel,
  removeWorkoutSet,
  replicateWorkoutSetFields,
  selectWorkoutModel,
  selectedModelIndex,
  setSpecialSet,
  specialSet,
  stateModels,
  updateWorkoutModel,
  updateWorkoutSet,
}) => {
  const formMethods = useForm<TSpecialSetInput>({
    resolver: yupResolver(workoutSpecialSetSchema),
    delayError: 800,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  })

  switch (mobilePage?.toString()) {
    case '2':
      return (
        <WorkoutModelsSets
          addWorkoutSet={addWorkoutSet}
          changePage={changePage}
          filters={filters}
          formMethods={formMethods}
          inputValue={inputValue}
          loadMoreExercises={loadMoreExercises}
          methods={methods}
          onChangeFilters={onChangeFilters}
          onSearchChange={onSearchChange}
          removeExerciseFromWorkoutSet={removeExerciseFromWorkoutSet}
          removeFilter={removeFilter}
          removeWorkoutSet={removeWorkoutSet}
          replicateWorkoutSetFields={replicateWorkoutSetFields}
          selectedModelIndex={selectedModelIndex}
          setSpecialSet={setSpecialSet}
          stateModels={stateModels}
        />
      )
    case '3':
      return (
        <WorkoutModelsSpecialSet
          addExerciseToWorkoutSet={addExerciseToWorkoutSet}
          addWorkoutSet={addWorkoutSet}
          changePage={changePage}
          filters={filters}
          formMethods={formMethods}
          inputValue={inputValue}
          loadMoreExercises={loadMoreExercises}
          onChangeFilters={onChangeFilters}
          onSearchChange={onSearchChange}
          removeExerciseFromWorkoutSet={removeExerciseFromWorkoutSet}
          removeFilter={removeFilter}
          selectedModelIndex={selectedModelIndex}
          setSpecialSet={setSpecialSet}
          specialSet={specialSet}
          stateModels={stateModels}
          updateWorkoutSet={updateWorkoutSet}
        />
      )
    default:
      return (
        <WorkoutModelsRoutines
          addWorkoutModel={addWorkoutModel}
          changePage={changePage}
          removeWorkoutModel={removeWorkoutModel}
          selectWorkoutModel={selectWorkoutModel}
          updateWorkoutModel={updateWorkoutModel}
          stateModels={stateModels}
        />
      )
  }
}

export default Mobile
